import { Fragment } from 'react';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCachedAccess } from '@/lib/access/use-access';
import { NAV_PATHS } from '@/lib/access/route-permissions';
import { Hint } from '@/components/ui/hint';
import { Skeleton } from '@/components/ui/skeleton';
import { visibleSections } from './nav-model';
import { NotificationBell } from '@/components/topbar/notification-bell';

// The main menu COLLAPSED to a rail of icons, beside the full-screen views (PO
// 2026-10-27: «colapsaría el menú manteniendo los íconos pequeños a la izquierda»). A
// full-screen screen closing with its own ✕ did not say it was still inside the console,
// nor how to go elsewhere; the rail keeps the map on screen at the cost of 64 px.
//
// Desktop only (`lg` and up): on a phone or a tablet the full-screen views keep the
// whole width and NO menu (`hidden lg:flex`). The shell decides WHERE it stands
// (`app-shell.tsx`, `useImmersiveMode` in `focus-mode.ts`); this file only draws it.
//
// Same items, same order, same permission filter as the expanded sidebar — both read
// `nav-model.ts`. The section headings become thin dividers; each section stays a
// <nav> named by its heading, so a screen reader still hears «Operation», «Tools»…
//
// Every item is an icon-only link in a 44 × 44 target: its accessible name is the
// item's label (`aria-label`), and the console's own hint (`Hint`, never the browser
// `title`) shows that label on hover and on keyboard focus, to the right of the rail.
//
// The active route stays highlighted. The class is a STRING read off NavLink's
// `aria-current="page"`, not NavLink's function form: the hint's trigger is a Radix
// Slot, which joins classNames as strings and would print a function's source.
//
// No brand at the top: the expanded sidebar carries none either (the Nexa lockup lives
// in the top bar), and the full-screen views bring their own header.

const ITEM =
  'flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground';

export function SidebarRail() {
  const { t } = useTranslation('admin');
  const access = useCachedAccess();
  const sections = visibleSections(access.can);

  return (
    <aside
      aria-label={t('sidebar.mobileMenu')}
      data-testid="sidebar-rail"
      className="hidden min-h-0 w-16 shrink-0 flex-col border-r border-border bg-muted/40 lg:flex"
    >
      {/* The one scroll region of the rail (a short laptop screen with every section).
          `relative`: a scroll region holds its own absolutely placed bits (app-shell.tsx). */}
      <div className="relative flex min-h-0 flex-1 flex-col items-center overflow-y-auto py-2">
        {access.loading ? <RailSkeleton label={t('access.loading')} /> : null}
        {sections.map(({ id, titleKey, items }, index) => (
          <Fragment key={id}>
            {index > 0 ? (
              <div aria-hidden className="my-2 h-px w-8 shrink-0 bg-border" data-testid="rail-divider" />
            ) : null}
            <nav
              aria-label={t(titleKey)}
              data-testid={`rail-${id}`}
              className="flex flex-col items-center gap-1"
            >
              {items.map(({ id: navId, labelKey, icon: Icon }) => (
                <Hint key={navId} body={t(labelKey)} side="right">
                  <NavLink
                    to={NAV_PATHS[navId]}
                    aria-label={t(labelKey)}
                    data-testid={`rail-nav-${navId}`}
                    className={ITEM}
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                  </NavLink>
                </Hint>
              ))}
            </nav>
          </Fragment>
        ))}
      </div>
      {/* The console's bell, here where «Operar» has no top bar (operator-notifications U2):
          run notifications + the operator alerts in the operator's airports, ONE badge (it
          replaces U1's rail counter). At the foot, so it never shifts the menu. */}
      <div className="flex shrink-0 justify-center py-2" data-testid="rail-bell-slot">
        <NotificationBell rail />
      </div>
    </aside>
  );
}

/** LOADING — access not known yet: placeholder squares, never the wrong menu. */
function RailSkeleton({ label }: { label: string }) {
  return (
    <div
      className="flex flex-col items-center gap-1"
      role="status"
      aria-busy="true"
      data-testid="sidebar-rail-loading"
    >
      <span className="sr-only">{label}</span>
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-11 w-11" />
      ))}
    </div>
  );
}
